// this file contains selectors that are used to get the current state of the application.
// these don't belong in base, but are often used in other selector files and need to be in
// a more base module to avoid circular dependencies.

import {
  courseSelectors,
  createAppSelector,
  createStringArraySelector,
  sectionSelectors,
  selectCurrentSectionId,
  termSelectors,
} from "./base";

import { filterMap } from "utils/collections";
import { isLinkedSection, isTemplateSection } from "utils/publishing";

export const selectCurrentSection = createAppSelector(
  [selectCurrentSectionId, sectionSelectors.selectEntities],
  (sectionId, sections) => (sectionId && sectionId in sections ? sections[sectionId] : null),
);

/** Term ids with `archived_at` set. Order follows `termSelectors.selectAll`. */
export const selectArchivedTermIds = createStringArraySelector(
  [termSelectors.selectAll],
  (terms): string[] =>
    filterMap(
      terms,
      (term) => Boolean(term.archived_at),
      (term) => term.id,
    ),
);

/**
 * Section ids that are effectively archived: own `archived_at`, archived term, or (for linked
 * sections) archived template.
 */
export const selectArchivedSectionIds = createStringArraySelector(
  [sectionSelectors.selectAll, selectArchivedTermIds],
  (sections, archivedTermIds): string[] => {
    const archivedTermIdSet = new Set(archivedTermIds);
    const archivedTemplateIds = new Set(
      filterMap(
        sections,
        (section) => isTemplateSection(section) && Boolean(section.archived_at),
        (section) => section.id,
      ),
    );
    return filterMap(
      sections,
      (section) =>
        Boolean(
          section.archived_at ||
            (section.term_id && archivedTermIdSet.has(section.term_id)) ||
            (isLinkedSection(section) &&
              section.template_id &&
              archivedTemplateIds.has(section.template_id)),
        ),
      (section) => section.id,
    );
  },
);

// Lives here (rather than in section.ts) so lower-level selectors like edit-mode can reuse it
// without circular imports.
export const selectCurrentSectionIsArchived = createAppSelector(
  [selectCurrentSectionId, selectArchivedSectionIds],
  (sectionId, archivedSectionIds) => sectionId != null && archivedSectionIds.includes(sectionId),
);

export const selectCurrentCourse = createAppSelector(
  [selectCurrentSection, courseSelectors.selectEntities],
  (section, courses) =>
    section?.course_id && courses[section.course_id] ? courses[section.course_id] : null,
);

export const selectCourseForSectionId = createAppSelector(
  [sectionSelectors.selectById, courseSelectors.selectEntities],
  (section, courses) =>
    section?.course_id && section.course_id in courses ? courses[section.course_id] : null,
);
